<script setup lang="ts">
/* =============================================================
   AVAILABILITY — WHAT IS COMING, ACROSS EVERY BERTH
   =============================================================
   The owner's glance: for each of their berths, the blocked time
   and live bookings (requested or confirmed) that have not ended
   yet, in one list in date order. Anything not on it is open.

   Read-only on purpose. Blocking time is a button that opens the
   block form; removing a block or answering a request happens on
   the berth's own page or the booking, where the whole record is
   in view.

   One request per berth per table. An owner has a handful of
   berths, and lib/ already filters by berth -- a cross-berth
   query would be a new data function for no visible gain.
   ============================================================= */

import { computed, onMounted, ref } from 'vue'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import Alert from '@/components/ui/Alert.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import UpcomingList from '@/components/owner/UpcomingList.vue'
import type { UpcomingEntry } from '@/components/owner/UpcomingList.vue'
import { routes, to } from '@/config/routes'
import { listBerths } from '@/lib/berths'
import { listBlocks } from '@/lib/blocks'
import { listBookings } from '@/lib/bookings'
import { useAuth } from '@/lib/auth/useAuth'
import {
  blockReasonLabel,
  bookingStateLabel,
  bookingStateTone,
  formatDateTime,
  formatDuration,
} from '@/lib/bookingLabels'
import { errorMessage } from '@/lib/errors'
import { useDocumentTitle } from '@/lib/composables/useDocumentTitle'
import type { Berth } from '@/lib/types'

useDocumentTitle('Availability')

const { user } = useAuth()

interface BerthAgenda {
  berth: Berth
  entries: UpcomingEntry[]
  /** Requests still waiting on the owner's answer. */
  waiting: number
}

const agendas = ref<BerthAgenda[]>([])
const loading = ref(true)
const loadError = ref('')

/** How many events a berth shows before "and N more" points to its page. */
const SHOWN = 6

/* Short dates: the year only when it is not this one, so a line fits a phone. */
const thisYear = new Date().getFullYear()
const shortFmt = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
const short = (iso: string) =>
  new Date(iso).getFullYear() === thisYear ? shortFmt.format(new Date(iso)) : formatDateTime(iso)

const span = (start: string, end: string) =>
  `${short(start)} → ${short(end)} · ${formatDuration(start, end)}`

async function agendaFor(berth: Berth): Promise<BerthAgenda> {
  const now = Date.now()
  const [blocks, bookings] = await Promise.all([
    listBlocks(berth.id),
    listBookings({ berthId: berth.id }),
  ])
  const entries: UpcomingEntry[] = [
    ...blocks
      .filter((b) => new Date(b.ends_at).getTime() > now)
      .map<UpcomingEntry>((b) => ({
        key: `block-${b.id}`,
        kind: 'block',
        start: b.starts_at,
        end: b.ends_at,
        title: blockReasonLabel(b.reason),
        detail: span(b.starts_at, b.ends_at),
        badge: 'Blocked',
        tone: 'neutral',
        to: to.berthTab(berth.id, 'availability'),
      })),
    ...bookings
      .filter(
        (b) =>
          (b.status === 'requested' || b.status === 'confirmed') &&
          new Date(b.departure).getTime() > now,
      )
      .map<UpcomingEntry>((b) => ({
        key: `booking-${b.id}`,
        kind: 'booking',
        start: b.arrival,
        end: b.departure,
        title: b.vessel ? `${b.vessel.name} · ${b.reference}` : b.reference,
        detail: span(b.arrival, b.departure),
        // The generic label says "Awaiting owner"; here the owner is the reader.
        badge: b.status === 'requested' ? 'Needs your answer' : bookingStateLabel(b.status),
        tone: bookingStateTone(b.status),
        to: to.booking(b.id),
      })),
  ]
  entries.sort((a, b) => a.start.localeCompare(b.start))
  const waiting = bookings.filter(
    (b) => b.status === 'requested' && new Date(b.departure).getTime() > now,
  ).length
  return { berth, entries, waiting }
}

async function load(): Promise<void> {
  const companyId = user.value?.companyId
  if (companyId === undefined) return
  loading.value = true
  loadError.value = ''
  try {
    const berths = await listBerths(companyId)
    agendas.value = await Promise.all(berths.map(agendaFor))
  } catch (e) {
    loadError.value = errorMessage(e, 'Could not load availability.')
  } finally {
    loading.value = false
  }
}

onMounted(load)

const totals = computed(() => {
  const all = agendas.value.flatMap((a) => a.entries)
  return {
    waiting: agendas.value.reduce((n, a) => n + a.waiting, 0),
    bookings: all.filter((e) => e.kind === 'booking').length,
    blocks: all.filter((e) => e.kind === 'block').length,
  }
})

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`
</script>

<template>
  <PageHeader
    title="Availability"
    description="What is coming up at each of your berths. Anything not listed is open for booking."
  />

  <Alert v-if="loadError" tone="danger" class="page-alert">{{ loadError }}</Alert>

  <p v-if="loading && agendas.length === 0" class="page-status">Loading…</p>

  <Panel v-else-if="agendas.length === 0 && !loadError">
    <EmptyState icon="calendar" title="No locations yet" description="Add a berth to see its bookings and blocked time here.">
      <template #action>
        <Button icon="plus" :to="routes.locationNew">Add location</Button>
      </template>
    </EmptyState>
  </Panel>

  <template v-else-if="agendas.length > 0">
    <ul class="summary" aria-label="Summary">
      <li class="chip">
        <strong>{{ totals.bookings }}</strong> upcoming {{ totals.bookings === 1 ? 'booking' : 'bookings' }}
      </li>
      <li v-if="totals.waiting > 0" class="chip chipWarn">
        <strong>{{ totals.waiting }}</strong> awaiting your answer
      </li>
      <li class="chip">
        <strong>{{ totals.blocks }}</strong> {{ totals.blocks === 1 ? 'block' : 'blocks' }}
      </li>
    </ul>

    <div class="berths">
      <Panel
        v-for="a in agendas"
        :key="a.berth.id"
        flush
        :title="a.berth.name"
        :description="
          a.entries.length === 0
            ? `${a.berth.city} · open, nothing planned`
            : `${a.berth.city} · ${plural(a.entries.length, 'event', 'events')} ahead`
        "
      >
        <template #actions>
          <Button size="sm" variant="ghost" :to="to.berthTab(a.berth.id, 'availability')">Open</Button>
          <Button size="sm" icon="plus" :to="to.berthBlockNew(a.berth.id)">Block time</Button>
        </template>

        <p v-if="a.entries.length === 0" class="panel-status">No bookings or blocked time ahead.</p>
        <template v-else>
          <UpcomingList :entries="a.entries.slice(0, SHOWN)" />
          <RouterLink v-if="a.entries.length > SHOWN" :to="to.berthTab(a.berth.id, 'availability')" class="more">
            And {{ a.entries.length - SHOWN }} more
          </RouterLink>
        </template>
      </Panel>
    </div>
  </template>
</template>

<style scoped>
.summary {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2xs);
  margin: 0 0 var(--space-md);
  padding: 0;
  list-style: none;
}

.chip {
  padding: var(--space-3xs) var(--space-sm);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
  background-color: var(--color-surface);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-full);
}

.chip strong {
  color: var(--color-heading);
  font-variant-numeric: tabular-nums;
}

.chipWarn {
  color: var(--color-accent-2);
  background-color: var(--color-accent-2-soft);
  border-color: transparent;
}

.chipWarn strong {
  color: inherit;
}

.berths {
  display: grid;
  gap: var(--space-md);
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 28rem), 1fr));
  align-items: start;
}

.more {
  display: block;
  padding: var(--space-xs) var(--space-md);
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  border-top: var(--border-width) solid var(--color-border);
}
</style>
